import {FC, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {AlertTriangle, Check, RotateCcw, ShieldCheck} from 'lucide-react';
import {
  Button,
  CaptureCamera,
  CaptureFrame,
  type CaptureStatus,
  cn,
  WebcamErrorType,
} from '@chekinapp/ui';
import {IDENTITY_VERIFICATION_PHOTOS_MAX_SIZE_MB, LivenessAction} from '@chekin/core';
import {useLivenessStore, useProgressStore} from './store';
import {useLightingGate, useLivenessDetection} from './hooks';
import {LIVENESS_STEP_HINT_AFTER_MS, LIVENESS_STEP_TIMEOUT_MS} from './constants';
import {useErrorModal} from '../../hooks';
import {CameraDeniedPanel} from '../capture/CameraDeniedPanel';
import {FaceGuide} from '../capture/FaceGuide';
import {CaptureWaitNote} from '../capture/CaptureWaitNote';
import {OverlayRetryButton} from '../capture/OverlayRetryButton';
import {QuickFixes} from '../capture/QuickFixes';
import {VerifyLaterLink} from '../capture/VerifyLaterLink';
import {useIVStore} from '../../store';
import {Analytics, ANALYTIC_EVENTS} from '../../analytics';
import {reportCameraError} from '../../utils/reportCameraError';
import {
  ChallengeCueIcon,
  ChallengeCueStyles,
  ChallengeDots,
  Container,
  SpecialIndicator,
} from './components';
import {LivenessStep, StepStatus} from './types';

const videoConstraints = {
  facingMode: 'user',
  // width: {min: 320, ideal: 1280, max: 1920},
  // height: {min: 240, ideal: 720, max: 1080},
  width: {min: 320, ideal: 640, max: 800},
  height: {min: 240, ideal: 480, max: 600},
  frameRate: {ideal: 30, max: 30},
};

const WEBCAM_QUALITY = {
  minScreenshotW: 800,
  minScreenshotH: 600,
  screenshotMaxSize: IDENTITY_VERIFICATION_PHOTOS_MAX_SIZE_MB,
  imageSmoothing: false,
} as const;

const getCaptureStatus = (stepStatus: StepStatus): CaptureStatus =>
  stepStatus === 'success' || stepStatus === 'error' ? stepStatus : 'scanning';

const INSTRUCTION_KEY: Record<LivenessStep, string> = {
  LOOK_STRAIGHT: 'liveness_detection.instructions.look_straight',
  TURN_LEFT: 'liveness_detection.instructions.turn_left',
  TURN_RIGHT: 'liveness_detection.instructions.turn_right',
  TURN_UP: 'liveness_detection.instructions.turn_up',
  BLINK: 'liveness_detection.instructions.blink',
  COMPLETED: 'liveness_detection.instructions.completed',
};

const HINT_KEY: Partial<Record<LivenessStep, string>> = {
  LOOK_STRAIGHT: 'liveness_detection.hints.look_straight',
  TURN_LEFT: 'liveness_detection.hints.turn',
  TURN_RIGHT: 'liveness_detection.hints.turn',
  TURN_UP: 'liveness_detection.hints.turn_up',
  BLINK: 'liveness_detection.hints.blink',
};

export type LivenessDetectionProps = {
  onComplete?: (data: {
    screenshot: string | null;
    recordingVideo: Blob | null;
    performedBy: 'CLIENT' | 'SERVER';
    completedActions: LivenessAction[];
  }) => void;
  isRequired: boolean;
  onVerifyLater: () => void;
  forceLivenessMechanism?: 'AUTO' | 'SERVER' | 'CLIENT';
  className?: string;
  faceGuideSize?: 'default' | 'max';
};

export const LivenessDetection: FC<LivenessDetectionProps> = ({
  onComplete,
  className,
  isRequired,
  onVerifyLater,
  forceLivenessMechanism = 'AUTO',
  faceGuideSize = 'default',
}) => {
  const {
    videoRef,
    isModelLoading,
    startLivenessDetection,
    faceScreenshot,
    isUsingClientDetection,
  } = useLivenessDetection({forceLivenessMechanism});
  const {t} = useTranslation('identity-verification');
  const isCompleteRef = useRef(false);

  const currentStep = useLivenessStore(state => state.currentStep);
  const stepStatus = useLivenessStore(state => state.stepStatus);
  const progress = useProgressStore(state => state.progress);
  const recordingVideo = useLivenessStore(state => state.recordingVideo);
  const completedActions = useLivenessStore(state => state.completedActions);
  const performedBy = isUsingClientDetection ? 'CLIENT' : 'SERVER';
  const {setIsIvFailed} = useIVStore(state => state);
  const [isFailedLivenessTriggered, setIsFailedLivenessTriggered] = useState(false);
  const [isHintVisible, setIsHintVisible] = useState(false);
  const [isCameraDenied, setIsCameraDenied] = useState(false);
  const {displayError, ErrorModal} = useErrorModal();

  const {isTooDark, isGatePassed, startLightingGate} = useLightingGate({videoRef});

  useEffect(() => {
    if (isGatePassed) startLivenessDetection();
  }, [isGatePassed, startLivenessDetection]);

  const isStepClockRunning =
    !isModelLoading &&
    !isFailedLivenessTriggered &&
    currentStep !== 'COMPLETED' &&
    (stepStatus === 'progress' || stepStatus === 'error');

  const showChallengeProgress =
    stepStatus === 'progress' &&
    currentStep !== 'LOOK_STRAIGHT' &&
    currentStep !== 'COMPLETED' &&
    progress != null;

  const mechanismTrackedRef = useRef(false);
  useEffect(() => {
    if (isModelLoading || isUsingClientDetection === undefined) return;
    if (mechanismTrackedRef.current) return;
    mechanismTrackedRef.current = true;
    Analytics.track(ANALYTIC_EVENTS.ivFaceDetectionMechanism, {
      flow: 'liveness',
      mechanism: performedBy,
    });
  }, [isModelLoading, isUsingClientDetection, performedBy]);

  const failedMetaRef = useRef({performedBy, completedActions});
  failedMetaRef.current = {performedBy, completedActions};

  useEffect(() => {
    setIsHintVisible(false);
    if (!isStepClockRunning) return;

    const hintTimeout = setTimeout(
      () => setIsHintVisible(true),
      LIVENESS_STEP_HINT_AFTER_MS,
    );
    const failTimeout = setTimeout(() => {
      setIsFailedLivenessTriggered(true);
      const {performedBy: mechanism, completedActions: actions} = failedMetaRef.current;
      Analytics.track(ANALYTIC_EVENTS.ivLivenessFailed, {
        mechanism,
        reason: 'timeout',
        step: currentStep,
        completed_actions: actions.length ? actions.join(',') : null,
      });
    }, LIVENESS_STEP_TIMEOUT_MS);

    return () => {
      clearTimeout(hintTimeout);
      clearTimeout(failTimeout);
    };
  }, [currentStep, isStepClockRunning]);

  const isComplete =
    !isCompleteRef.current &&
    currentStep === 'COMPLETED' &&
    stepStatus === 'success' &&
    onComplete &&
    !isFailedLivenessTriggered;

  useEffect(() => {
    if (isComplete) {
      onComplete({
        screenshot: faceScreenshot,
        recordingVideo,
        completedActions,
        performedBy,
      });
      isCompleteRef.current = true;
    }
  }, [
    isComplete,
    faceScreenshot,
    recordingVideo,
    onComplete,
    completedActions,
    performedBy,
  ]);

  const handleTryAgain = () => {
    setIsFailedLivenessTriggered(false);
    startLightingGate();
    isCompleteRef.current = false;
  };

  const handleVerifyLater = () => {
    setIsIvFailed(true);
    onVerifyLater();
  };

  const handleRetryCamera = () => {
    setIsCameraDenied(false);
    void navigator.mediaDevices
      ?.getUserMedia({video: true})
      .catch(() => setIsCameraDenied(true));
  };

  const isCompletedStep = currentStep === 'COMPLETED';

  if (isCameraDenied) {
    return (
      <Container className={className}>
        <div className="flex flex-col items-center gap-5">
          <CameraDeniedPanel />
          <Button onClick={handleRetryCamera} className="w-full max-w-[340px] gap-2">
            <RotateCcw className="h-[18px] w-[18px]" />
            {t('try_again')}
          </Button>
          {!isRequired && <VerifyLaterLink onClick={handleVerifyLater} />}
        </div>
      </Container>
    );
  }

  return (
    <Container className={className}>
      <ChallengeCueStyles />
      <CaptureCamera
        ref={videoRef}
        variant="face"
        showScanLine={false}
        status={isFailedLivenessTriggered ? 'error' : getCaptureStatus(stepStatus)}
        additionalConstraints={videoConstraints}
        handlePermissionDenied={false}
        onReady={startLightingGate}
        onError={error => {
          reportCameraError(error, 'liveness');
          if (error.type === WebcamErrorType.Permission) {
            setIsCameraDenied(true);
            return;
          }
          displayError(error);
        }}
        overlay={
          isFailedLivenessTriggered ? undefined : (
            <FaceGuide
              size={faceGuideSize}
              state={stepStatus === 'success' ? 'centered' : 'scanning'}
              progress={showChallengeProgress ? progress : undefined}
            />
          )
        }
        {...WEBCAM_QUALITY}
      >
        {!isUsingClientDetection && !isFailedLivenessTriggered && <SpecialIndicator />}

        {!isFailedLivenessTriggered && (
          <>
            <CaptureFrame.Status
              className={cn(stepStatus === 'error' && 'text-[var(--chekin-red-soft)]')}
            >
              {isTooDark ? (
                <>
                  <CaptureFrame.Dot tone="error" />
                  {t('liveness_detection.too_dark')}
                </>
              ) : isCompletedStep ? (
                <>
                  <CaptureFrame.Spinner />
                  {t('verification_in_progress')}
                </>
              ) : (
                <>
                  <ChallengeCueIcon step={currentStep} status={stepStatus} />
                  {t(
                    (isHintVisible && HINT_KEY[currentStep]) ||
                      INSTRUCTION_KEY[currentStep],
                  )}
                </>
              )}
            </CaptureFrame.Status>
            <ChallengeDots step={currentStep} status={stepStatus} />
          </>
        )}

        {isCompletedStep && (
          <CaptureFrame.Overlay
            status="success"
            icon={<Check className="h-[30px] w-[30px]" strokeWidth={3} />}
            title={t('liveness_detection.all_challenges_passed')}
          />
        )}

        {isFailedLivenessTriggered && (
          <>
            <CaptureFrame.Status>
              <CaptureFrame.Dot tone="error" />
              {t('validation_failed')}
            </CaptureFrame.Status>
            <CaptureFrame.Overlay
              status="error"
              icon={<AlertTriangle className="h-7 w-7" strokeWidth={2.5} />}
              title={t('liveness_detection.detection_failed')}
              description={t('liveness_detection.detection_failed_reasons')}
              action={<OverlayRetryButton onClick={handleTryAgain} />}
            />
          </>
        )}
      </CaptureCamera>

      {isFailedLivenessTriggered ? (
        <>
          <QuickFixes variant="liveness" className="mt-6" />
          {!isRequired && (
            <VerifyLaterLink onClick={handleVerifyLater} className="mt-6" />
          )}
        </>
      ) : isCompletedStep ? (
        <CaptureWaitNote className="mt-8" />
      ) : (
        <p className="m-0 mt-8 flex items-center justify-center gap-1.5 text-center text-[13px] font-medium text-[var(--chekin-color-gray-1)]">
          <ShieldCheck className="h-[18px] w-[18px] flex-none" />
          {t('selfie_blurred_and_encrypted_note')}
        </p>
      )}
      <ErrorModal />
    </Container>
  );
};
